import { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const fmt = (n) => `$${Math.round(n).toLocaleString("en-AU")}`;

export function Group({ id, title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-slate-200 py-4" data-testid={`filter-group-${id}`}>
      <button type="button" aria-expanded={open} aria-controls={`fg-${id}`} onClick={() => setOpen(!open)} data-testid={`filter-group-${id}-toggle`}
        className="w-full flex items-center justify-between text-left text-[13px] font-extrabold uppercase tracking-wide text-[var(--brand-primary)] focus-visible:outline-2 focus-visible:outline-[var(--brand-focus)] rounded">
        <span>{title}{count ? <span className="ml-2 rounded-full bg-[var(--brand-primary-tint)] px-2 py-0.5 text-[11px]">{count}</span> : null}</span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && <div id={`fg-${id}`} className="mt-3">{children}</div>}
    </section>
  );
}

export function Option({ testid, label, count, checked, disabled, onChange, radio }) {
  return (
    <label data-testid={testid} className={`flex items-center gap-2.5 py-1 text-sm cursor-pointer select-none ${disabled && !checked ? "opacity-40 cursor-not-allowed" : "hover:text-[var(--brand-primary)]"}`}>
      <input type={radio ? "radio" : "checkbox"} checked={!!checked} disabled={disabled && !checked} onChange={onChange}
        className="h-4 w-4 accent-[var(--brand-button)] rounded" />
      <span className="flex-1 min-w-0 truncate">{label}</span>
      <span className="text-xs tabular-nums text-slate-400" aria-label={`${count} products`}>{count.toLocaleString("en-AU")}</span>
    </label>
  );
}

export function CategoryTree({ facet, onPick, base }) {
  const [all, setAll] = useState(false);
  if (!facet) return null;
  const opts = all ? facet.options : facet.options.slice(0, 8);
  const crumbs = facet.breadcrumbs || [];
  return (
    <div data-testid="filter-category">
      {crumbs.filter((c) => c.in_context).length > 0 && (
        <nav aria-label="Category path" className="mb-2 flex flex-wrap items-center gap-1 text-xs text-slate-500">
          <button type="button" data-testid="filter-category-all" onClick={() => onPick(null)} className="hover:text-[var(--brand-link)] underline-offset-2 hover:underline">{base}</button>
          {crumbs.filter((c) => c.in_context).map((c, i, arr) => (
            <span key={c.slug} className="flex items-center gap-1">›
              {i === arr.length - 1 ? <b className="text-[var(--brand-primary)]">{(c.display_name)}</b>
                : <button type="button" onClick={() => onPick(c.slug)} className="hover:text-[var(--brand-link)] hover:underline">{(c.display_name)}</button>}
            </span>))}
        </nav>)}
      <ul className="space-y-0.5">
        {opts.map((o) => (
          <li key={o.slug}>
            <button type="button" data-testid={`filter-category-${o.slug}`} onClick={() => onPick(o.slug)}
              className="w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-left hover:bg-[var(--brand-primary-tint)] focus-visible:outline-2 focus-visible:outline-[var(--brand-focus)]">
              <span className="truncate">{o.display_name}</span>
              <span className="text-xs tabular-nums text-slate-400">{o.count.toLocaleString("en-AU")}</span>
            </button>
          </li>))}
      </ul>
      {facet.options.length > 8 && <button type="button" data-testid="filter-category-more" onClick={() => setAll(!all)} className="mt-1 text-xs font-bold text-[var(--brand-link)]">{all ? "Show fewer" : `Show all ${facet.options.length}`}</button>}
    </div>
  );
}

export function BrandFilter({ brands, selected, onToggle }) {
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  const sorted = useMemo(() => [...brands].sort((a, b) => b.count - a.count || a.display_name.localeCompare(b.display_name)), [brands]);
  const hit = q ? sorted.filter((b) => b.display_name.toLowerCase().includes(q.toLowerCase())) : sorted;
  const shown = all || q ? hit : hit.slice(0, 8);
  return (
    <div data-testid="filter-brand">
      {brands.length > 8 && (
        <div className="relative mb-2">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" aria-hidden="true" />
          <input data-testid="filter-brand-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${brands.length} brands`} aria-label="Search brands"
            className="w-full rounded-lg border border-slate-300 py-1.5 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-focus)]" />
        </div>)}
      {shown.map((b) => <Option key={b.key} testid={`facet-brand-${b.slug || b.key}`} label={b.display_name} count={b.count}
        checked={selected.includes(b.slug) || b.selected} disabled={b.count === 0} onChange={() => onToggle(b.slug || b.key)} />)}
      {!q && hit.length > 8 && <button type="button" data-testid="filter-brand-more" onClick={() => setAll(!all)} className="mt-1 text-xs font-bold text-[var(--brand-link)]">{all ? "Show top brands" : `Show all ${hit.length}`}</button>}
      {q && !hit.length && <div className="text-xs text-slate-400">No brand matches “{q}”.</div>}
    </div>
  );
}

export function PriceFilter({ hist, min, max, gst, onPrice, onGst }) {
  const lo = hist?.min ?? 0, hi = hist?.max ?? 0;
  const [val, setVal] = useState(null);
  const cur = val || [min ?? lo, Math.min(max ?? hi, hi)];
  const peak = Math.max(1, ...(hist?.bins || [0]));
  if (!hist || !hist.bins.length) return <div className="text-xs text-slate-400">No prices in these results.</div>;
  const step = Math.max(1, Math.round((hi - lo) / 200));
  const w = (hi - lo) / hist.bins.length;
  return (
    <div data-testid="filter-price">
      <div className="flex h-12 items-end gap-[2px]" aria-hidden="true" data-testid="filter-price-hist">
        {hist.bins.map((c, i) => {
          const a = lo + i * w, inRange = a + w >= cur[0] && a <= cur[1];
          return <div key={i} className={`flex-1 rounded-t-sm transition-colors ${inRange ? "bg-[var(--brand-accent)]" : "bg-slate-200"}`} style={{ height: `${Math.max(4, (c / peak) * 100)}%` }} />;
        })}
      </div>
      <Slider data-testid="filter-price-slider" className="mt-2" min={lo} max={hi} step={step} value={cur} minStepsBetweenThumbs={1}
        onValueChange={setVal} onValueCommit={(v) => { setVal(null); onPrice(v[0] > lo ? Math.floor(v[0]) : null, v[1] < hi ? Math.ceil(v[1]) : null); }}
        aria-label="Price range" />
      <form className="mt-3 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.target); onPrice(f.get("min") || null, f.get("max") || null); }}>
        <input name="min" key={`mn${min}`} defaultValue={min ?? ""} placeholder={fmt(lo)} inputMode="numeric" aria-label="Minimum price" data-testid="facet-price-min"
          className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        <span className="text-slate-400">–</span>
        <input name="max" key={`mx${max}`} defaultValue={max ?? ""} placeholder={hist.abs_max > hi ? `${fmt(hi)}+` : fmt(hi)} inputMode="numeric" aria-label="Maximum price" data-testid="facet-price-max"
          className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        <button type="submit" data-testid="facet-price-apply" className="rounded-lg bg-[var(--brand-button)] px-3 py-1.5 text-sm font-bold text-[var(--brand-on-button)] hover:bg-[var(--brand-button-hover)]">Go</button>
      </form>
      <div className="mt-1 text-[11px] text-slate-400">Prices {gst === "ex" ? "exclude" : "include"} GST{gst === "ex" ? "" : ", as on the cards"}.</div>
    </div>
  );
}
